import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ApiService } from '../core/services/api.service';
import { AuthService } from '../core/services/auth.service';
import { FunctionsService } from '../core/services/functions.service';
@Injectable({
    providedIn: 'root'
})
export class EventsApiService {
    constructor(
        private apiService: ApiService,
        public authService: AuthService,
        private _functions: FunctionsService
    ) { }
    private destroy$ = new Subject<void>();

    getEvent(data: any): Observable<any> {
        return this.authService.token$.pipe(
            takeUntil(this.destroy$),
            switchMap(token => {
                data.token = token;
                return this.apiService.post(`/events/get-events`, data);
            }),
            catchError(err => {
                console.error('Event fetch failed:', err);
                return of(null);
            })
        );
    }

    getDatebyMonth(month: number, year: number): Observable<any> {
        return this.apiService.get(`/events/get-dates/${month}?year=${year}?_=${Date.now()}`);
    };

    getEventTags(): Observable<any> {
        return this.apiService.get(`/events/get-tags?_=${Date.now()}`);
    };

    getEventLocations(): Observable<any> {
        return this.apiService.get(`/events/get-locations?_=${Date.now()}`);
    };

    createEvent(data: any, formData: FormData): Observable<any> {
        const token = this.authService.getToken();
        data.token = token;

        return this.apiService.post(`/events/create-new-event`, data, true).pipe(
            switchMap((createEventResponse) => {
                const responseData = createEventResponse;
                if (data.pictureKey && (data.pictureKey.endsWith('.jpg') || data.pictureKey.endsWith('.png'))) {
                    return this.apiService.upload('/users/upload-picture', formData, true).pipe(
                        map(() => responseData)
                    );
                }
                return of(responseData);
            }),
            catchError(error => {
                console.error('Create Post failed', error);
                let errorResponse = this._functions.decryptData(error.error?.data || '{}');
                return of({ error: errorResponse });
            })
        );
    }

    editEvent(data: any, formData: FormData): Observable<any> {
        const token = this.authService.getToken();
        data.token = token;

        return this.apiService.post(`/events/edit-event`, data, true).pipe(
            switchMap((editEventResponse) => {
                const responseData = editEventResponse;

                const shouldUploadPicture =
                    typeof data?.pictureKey === 'string' &&
                    (data.pictureKey.includes('.jpg') || data.pictureKey.includes('.png')) &&
                    data.pictureKey !== data.oldPictureKey;

                if (shouldUploadPicture && data.updatePicture) {
                    return this.apiService.post(`/users/delete-event-picture`, data, true).pipe(
                        switchMap(() =>
                            this.apiService.upload('/users/upload-picture', formData, true).pipe(
                                map(() => responseData) // return original response after upload
                            )
                        )
                    );
                } else if (shouldUploadPicture) {
                    return this.apiService.upload('/users/upload-picture', formData, true).pipe(
                        map(() => responseData)
                    );
                }

                // No picture upload required
                return of(responseData);
            }),
            catchError(error => {
                console.error('Edit Events failed', error);
                let errorResponse = this._functions.decryptData(error.error?.data || '{}');
                return of({ error: errorResponse });
            })
        );
    }

    deleteEvent(_id: string): Observable<any> {
        const token = this.authService.getToken();
        return this.apiService.delete(`/events/delete-event`, { _id, token }, true);
    };

    deleteEventVideo(_id: string,videoId:string): Observable<any> {
        const token = this.authService.getToken();
        return this.apiService.delete(`/events/remove-video`, { _id,videoId, token }, true);
    };

    getEventDetails(id: string): Observable<any> {
        return this.apiService.get(`/events/get-event-details/${id}?_=${Date.now()}`);
    };

    addNewVideo(data: any): Observable<any> {
        const token = this.authService.getToken();
        data.token = token;
        return this.apiService.post(`/events/add-new-video`, data, true);
    };
    editEventVideo(data: any): Observable<any> {
        const token = this.authService.getToken();
        data.token = token;
        return this.apiService.post(`/events/edit-video`, data, true);
    };

    toggleUpvote(eventId: string): Observable<any> {
        const token = this.authService.getToken();
        const userId = this.authService.getUser()?.id;
        return this.apiService.post(`/events/upvote/${eventId}`, { userId, token }, true);
    }

    toggleAttend(eventId: string): Observable<any> {
        const token = this.authService.getToken();
        const userId = this.authService.getUser()?.id;
        return this.apiService.post(`/events/attend/${eventId}`, { userId, token }, true);
    }


}
